import { ApiMethod } from "../models/api-types.type";
import type { NetworkBrandStatus } from "../models/network.model";
import { useAuthContext } from "./use-auth-context.hook";

/**
 * The Brand Portal — what shoppers do with a manufacturer's products across
 * every shop that sells them. Endpoints under services/pms/brand
 * (pms-api-v2 src/pms/pms-network/brand), for brand accounts only: a retailer
 * gets 403, and the backoffice signs a user out on an unexplained 403, so call
 * these only when `isBrandAccount(user)`.
 *
 * Every number here has already been through the API's privacy rules
 * (brand-privacy.ts): no shopper, no email, a shop's name only when it agreed
 * to be listed, and nothing resting on fewer than three shoppers or on one
 * unlisted shop.
 */
const BASE = '/services/pms/brand';

export type BrandPeriod = '7d' | '30d' | '90d';
export const BRAND_PERIODS: BrandPeriod[] = ['7d', '30d', '90d'];

export const DEMAND_DIMENSIONS = ['product', 'collection', 'country', 'city', 'roomType', 'colorFamily', 'appearance', 'format', 'week'] as const;
export type DemandDimension = (typeof DEMAND_DIMENSIONS)[number];

export type SuppressionReason = 'no_retailers' | 'no_demand' | 'single_unlisted_shop' | 'too_few_shoppers' | 'too_few_retailers' | 'dominated';

/** How the API names a shop: by name only when it agreed to be listed. */
export type ShopLabel =
    | { kind: 'listed'; name: string; city?: string | null; country?: string | null }
    | { kind: 'anonymous'; country?: string | null };

export interface BrandInfo {
    id: string;
    slug: string;
    name: string;
    status: NetworkBrandStatus;
    country?: string | null;
    logoUrl?: string | null;
    materials: string[];
}

export interface BrandMe {
    connected: boolean;
    brand: BrandInfo | null;
    products: number;
    digitized: number;
    collections: Array<{ id: string; name: string }>;
}

export interface BrandPeriodInfo {
    days: number;
    from: string;
    to: string;
    previousFrom: string;
    previousTo: string;
}

export interface MetricValue {
    value: number | null;
    previous: number | null;
    change: number | null;
}

export interface DemandRow {
    key: string;
    label: string;
    product?: { uuid: string; name: string; thumbnailUrl?: string | null; collectionName?: string | null; ref?: string | null };
    city?: string;
    country?: string | null;
    visualizations: number;
    shoppers: number;
    favourites: number;
    buyClicks: number;
    leads: number | null;
    renders: number;
    downloads: number;
    retailers: number;
    previous: { visualizations: number; shoppers: number; leads: number | null } | null;
    change: { visualizations: number | null; shoppers: number | null; leads: number | null };
    folded?: number;
}

export interface SeriesPoint {
    key: string;
    visualizations: number | null;
    shoppers: number | null;
    previous: number | null;
}

export interface RetailerCounts {
    total: number;
    listed: number;
    anonymous: number;
    sharingDemand: number;
}

export interface BrandOverview {
    brand: BrandInfo;
    period: BrandPeriodInfo;
    catalogue: { products: number; digitized: number; collections: number; retailers: RetailerCounts };
    suppressed: { reason: SuppressionReason } | null;
    kpis: Record<'visualizations' | 'shoppers' | 'roomUploads' | 'favourites' | 'buyClicks' | 'leads' | 'renders' | 'downloads', MetricValue> | null;
    granularity?: 'day' | 'week';
    trend: SeriesPoint[];
    topProducts: DemandRow[];
    topRegions: DemandRow[];
    topCities?: DemandRow[];
    rising: DemandRow[];
}

export interface BrandDemand {
    period: BrandPeriodInfo;
    groupBy: DemandDimension;
    suppressed: { reason: SuppressionReason } | null;
    rows: DemandRow[];
    other: DemandRow | null;
    leadsAvailable: boolean;
}

export interface Breakdown {
    rows: DemandRow[];
    other: DemandRow | null;
    withheld: boolean;
}

export interface RetailerRow {
    label: ShopLabel;
    sharesDemand: boolean;
    visualizations: number | null;
    shoppers: number | null;
    leads: number | null;
}

export interface BrandProduct {
    period: BrandPeriodInfo;
    product: {
        uuid: string;
        name: string;
        ref?: string | null;
        collectionId?: string | null;
        collectionName?: string | null;
        appearance?: string | null;
        material?: string | null;
        colorFamily?: string | null;
        colourName?: string | null;
        colourHex?: string | null;
        format?: string | null;
        finish?: string | null;
        thumbnailUrl?: string | null;
        digitized: boolean;
        retailerCount: number;
    };
    suppressed: { reason: SuppressionReason } | null;
    kpis: Record<'visualizations' | 'shoppers' | 'favourites' | 'buyClicks' | 'leads' | 'renders', MetricValue> | null;
    granularity?: 'day' | 'week';
    trend: SeriesPoint[];
    regions: Breakdown;
    cities: Breakdown;
    roomTypes: Breakdown;
    funnel: Array<{ key: 'visualized' | 'favourited' | 'converted'; shoppers: number | null }>;
    retailers: { rows: RetailerRow[]; otherRetailers: { retailers: number; visualizations: number; shoppers: number } | null };
}

export interface BenchmarkRow {
    country: string;
    category: string;
    share: number;
    previousShare: number | null;
    changePts: number | null;
    visualizations: number;
    marketVisualizations: number;
    competitors: number;
}

export interface BrandBenchmark {
    period: BrandPeriodInfo;
    basis: 'appearance' | 'material';
    brandMaterial: string | null;
    headline: BenchmarkRow | null;
    rows: BenchmarkRow[];
    withheld: number;
    coverage: { placedProducts: number; taggedByVision: number };
}

export interface DistributionCollection {
    id: string;
    name: string;
    appearance?: string | null;
    products: number;
    retailers: number;
    fullRange: number;
    gaps: Array<{ label: ShopLabel; carried: number; missing: Array<{ uuid: string; name: string }> }>;
    retailerRows: Array<{ label: ShopLabel; carried: number; missing: Array<{ uuid: string; name: string }> }>;
}

export interface BrandDistribution {
    period: BrandPeriodInfo;
    retailers: RetailerCounts;
    collections: DistributionCollection[];
    products: Array<{ uuid: string; name: string; thumbnailUrl?: string | null; collectionName?: string | null; digitized: boolean; retailers: number; listedRetailers: ShopLabel[] }>;
    demandGaps: Array<{
        product: { uuid: string; name: string; thumbnailUrl?: string | null };
        city: string;
        country: string | null;
        visualizations: number;
        shoppers: number;
        nearest: { label: ShopLabel; distanceKm: number } | null;
    }>;
    gapDistanceKm: number;
}

export interface UnmetGroup {
    key: string;
    colorFamily: string | null;
    appearance: string | null;
    country: string | null;
    city: string | null;
    material: string | null;
    searches: number;
    previous: number | null;
    change: number | null;
    retailers: number;
    weekly: Array<{ key: string; searches: number }>;
    closest: { uuid: string; name: string; thumbnailUrl?: string | null; similarity: number } | null;
}

export interface BrandUnmet {
    period: BrandPeriodInfo;
    groupBy: 'look' | 'country' | 'city';
    surfaceId: { model: string } | null;
    closestAvailable: boolean;
    total: number | null;
    groups: UnmetGroup[];
    other: { searches: number; folded: number } | null;
}

export interface BrandAnnouncement {
    uuid: string;
    title: string;
    body: string;
    products: Array<{ uuid: string; name: string | null; thumbnailUrl?: string | null }>;
    recipients: number;
    delivered: number;
    sentBy: string | null;
    createdAt: string;
}

export interface AnnouncementLimits {
    title: number;
    body: number;
    products: number;
    perDay: number;
}

const query = (params: Record<string, unknown>) => {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    return entries.length ? `?${entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}` : '';
};

/** Whether the signed-in vendor is a manufacturer (PMSVendor.kind === 'brand'). */
export const isBrandAccount = (user: unknown): boolean => (user as any)?.vendor?.kind === 'brand';

export const useBrandApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const send = <T,>(method: ApiMethod, path: string, body?: unknown) => sendAuthGuardedRequest(method, path, body) as Promise<T>;

    return {
        me: () => send<BrandMe>(ApiMethod.GET, `${BASE}/me`),
        overview: (period: BrandPeriod) => send<BrandOverview>(ApiMethod.GET, `${BASE}/overview${query({ period })}`),
        demand: (period: BrandPeriod, groupBy: DemandDimension) => send<BrandDemand>(ApiMethod.GET, `${BASE}/demand${query({ period, groupBy })}`),
        demandCsv: (period: BrandPeriod, groupBy: DemandDimension) =>
            sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/demand/export${query({ period, groupBy })}`, undefined, { responseType: 'arraybuffer' } as any) as Promise<ArrayBuffer>,
        product: (uuid: string, period: BrandPeriod) => send<BrandProduct>(ApiMethod.GET, `${BASE}/products/${encodeURIComponent(uuid)}${query({ period })}`),
        benchmark: (period: BrandPeriod, basis: 'appearance' | 'material' = 'appearance') => send<BrandBenchmark>(ApiMethod.GET, `${BASE}/benchmark${query({ period, basis })}`),
        distribution: (period: BrandPeriod) => send<BrandDistribution>(ApiMethod.GET, `${BASE}/distribution${query({ period })}`),
        unmet: (period: BrandPeriod, groupBy: 'look' | 'country' | 'city' = 'look') => send<BrandUnmet>(ApiMethod.GET, `${BASE}/unmet${query({ period, groupBy })}`),
        announcements: () => send<{ data: BrandAnnouncement[]; limits: AnnouncementLimits }>(ApiMethod.GET, `${BASE}/announcements`),
        announce: (input: { title: string; body: string; networkProductUuids: string[] }) => send<BrandAnnouncement>(ApiMethod.POST, `${BASE}/announcements`, input),
    };
};
